import React, { createContext, useContext, useMemo, useState, ReactNode } from 'react';

// Types
export type HighlightedScoreContextType = {
  highThreshold: number;
  lowThreshold: number;
  setHighThreshold: (value: number) => void;
  setLowThreshold: (value: number) => void;
  getHighColor: () => string;
  getLowColor: () => string;
  getDefaultColor: () => string;
};

const DEFAULT_HIGH_THRESHOLD = 70;
const DEFAULT_LOW_THRESHOLD = 30;
const HIGH_COLOR = '#4CAF50'; // Green
const LOW_COLOR = '#F44336'; // Red
const DEFAULT_COLOR = '#4786FC'; // Gray

const HighlightedScoreContext = createContext<HighlightedScoreContextType | undefined>(undefined);

export const HighlightedScoreProvider = ({ children }: { children: ReactNode }) => {
  const [highThreshold, setHighThreshold] = useState<number>(DEFAULT_HIGH_THRESHOLD);
  const [lowThreshold, setLowThreshold] = useState<number>(DEFAULT_LOW_THRESHOLD);

  const getHighColor = useMemo(() => () => HIGH_COLOR, []);
  const getLowColor = useMemo(() => () => LOW_COLOR, []);
  const getDefaultColor = useMemo(() => () => DEFAULT_COLOR, []);

  const contextValue = useMemo(() => ({
    highThreshold,
    lowThreshold,
    setHighThreshold,
    setLowThreshold,
    getHighColor,
    getLowColor,
    getDefaultColor,
  }), [highThreshold, lowThreshold, getHighColor, getLowColor, getDefaultColor]);

  return (
    <HighlightedScoreContext.Provider value={contextValue}>
      {children}
    </HighlightedScoreContext.Provider>
  );
};

export function useHighlightedScore() {
  const ctx = useContext(HighlightedScoreContext);
  if (!ctx) {
    throw new Error('useHighlightedScore must be used within HighlightedScoreProvider');
  }
  return ctx;
}
